/**
 * src/admin-ui/src/theme/ColorModeProvider.tsx
 *
 * Owns color-mode persistence and provides the configured MUI theme.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { ColorMode, createAppTheme } from './theme';

const STORAGE_KEY = 'theme';

interface ColorModeContextValue {
  mode: ColorMode;
  toggleColorMode: () => void;
}

interface ColorModeProviderProps {
  children: React.ReactNode;
}

const ColorModeContext = createContext<ColorModeContextValue | undefined>(undefined);

function isColorMode(value: string | null): value is ColorMode {
  return value === 'light' || value === 'dark';
}

function getInitialMode(): ColorMode {
  const storedMode = window.localStorage.getItem(STORAGE_KEY);
  if (isColorMode(storedMode)) {
    return storedMode;
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Provide color-mode state and the active MUI theme.
 *
 * :param children: Application subtree.
 * :returns: Theme and color-mode providers.
 */
export function ColorModeProvider({ children }: ColorModeProviderProps): React.ReactElement {
  const [mode, setMode] = useState<ColorMode>(getInitialMode);

  useEffect(() => {
    document.documentElement.dataset.theme = mode;
    document.documentElement.style.colorScheme = mode;
    const themeColor = mode === 'dark' ? '#1c130c' : '#D2691E';
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', themeColor);
  }, [mode]);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY && isColorMode(event.newValue)) {
        setMode(event.newValue);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemMode = (event: MediaQueryListEvent) => {
      if (!isColorMode(window.localStorage.getItem(STORAGE_KEY))) {
        setMode(event.matches ? 'dark' : 'light');
      }
    };
    mediaQuery.addEventListener('change', handleSystemMode);
    return () => mediaQuery.removeEventListener('change', handleSystemMode);
  }, []);

  const toggleColorMode = useCallback(() => {
    setMode((currentMode) => {
      const nextMode = currentMode === 'light' ? 'dark' : 'light';
      window.localStorage.setItem(STORAGE_KEY, nextMode);
      return nextMode;
    });
  }, []);

  const contextValue = useMemo(() => ({ mode, toggleColorMode }), [mode, toggleColorMode]);
  const theme = useMemo(() => createAppTheme(mode), [mode]);

  return (
    <ColorModeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

/**
 * Return the active color mode and toggle action.
 *
 * :returns: Color-mode context.
 * :raises Error: If called outside ``ColorModeProvider``.
 */
export function useColorMode(): ColorModeContextValue {
  const context = useContext(ColorModeContext);
  if (!context) {
    throw new Error('useColorMode must be used within ColorModeProvider');
  }
  return context;
}
